import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  MailOutlined,
  CodeOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { useDynamicApi } from "../../hooks/useApi";

const { Title, Text } = Typography;

const EmailTemplateDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to get token
  const getToken = () => {
    return (
      localStorage.getItem("adminToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Custom GET by ID function
  const customGetById = async (url) => {
    const token = getToken();
    if (!token) {
      Toast.error(t("common.error"));
      throw new Error("Authentication token not found");
    }

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t("emailManager.failedFetchDetails"));
      }

      return result;
    } catch (error) {
      console.error("GET by ID request failed:", error);
      throw error;
    }
  };

  // Load template details
  // const loadTemplateDetails = async () => {
  //   setLoading(true);
  //   try {
  //     const endpoint = `${API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID}/${id}`;
  //     const response = await customGetById(endpoint);

  //     if (response?.status === true && response.data) {
  //       setTemplate(response.data);
  //     } else {
  //       Toast.error(t("emailManager.failedFetchDetails"));
  //       navigate("/emailtemplate");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching template details:", error);
  //     Toast.error(error.message || t("emailManager.failedFetchDetails"));
  //     navigate("/emailtemplate");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const loadTemplateDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID}/${id}`;
      const response = await getTemplateById(endpoint);

      if (response?.status === true && response.data) {
        setTemplate(response.data);
      } else {
        Toast.error(t("emailManager.failedFetchDetails"));
        navigate("/emailtemplate");
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
      Toast.error(error.message || t("emailManager.failedFetchDetails"));
      navigate("/emailtemplate");
    } finally {
      setLoading(false);
    }
  };

  const { execute: getTemplateById, loading: fetching } = useDynamicApi("GET");

  useEffect(() => {
    if (id) {
      loadTemplateDetails();
    }
  }, [id]);

  // Format date
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // List of available variables for each template type
  const getTemplateVariables = (templateSlug) => {
    const variables = {
      "reset-password": [
        { name: "{{ name }}", description: t("emailManager.varUserName") },
        {
          name: "{{ reset_link }}",
          description: t("emailManager.varResetLink"),
        },
      ],
      "login-otp": [
        { name: "{{ user_name }}", description: t("emailManager.varUserName") },
        { name: "{{ otp }}", description: t("emailManager.varOtp") },
        {
          name: "{{ expiry_minutes }}",
          description: t("emailManager.varExpiryMinutes"),
        },
        {
          name: "{{ current_year }}",
          description: t("emailManager.varCurrentYear"),
        },
      ],
    };

    return (
      variables[templateSlug] || [
        { name: "{{ name }}", description: t("emailManager.varUserName") },
        { name: "{{ email }}", description: t("emailManager.varEmail") },
      ]
    );
  };

  if (loading) return null;

  if (!template) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>{t("emailManager.noTemplatesFound")}</Title>
          <Button type="primary" onClick={() => navigate("/emailtemplate")}>
            {t("common.previous")} {t("emailManager.templates")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="email-template-details-page">
      {/* Header with back button */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/emailtemplate")}
          >
            {t("common.previous")} {t("emailManager.templates")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("emailManager.templateDetails")}
          </Title>
        </Space>
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/email-templates/edit/${template.id}`)}
          style={{ background: "#52c41a", borderColor: "#52c41a" }}
        >
          {t("common.edit")}
        </Button> */}
      </div>

      {/* Main Content */}
      <Card>
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>{template.title}</h1>
            <Space size="middle" wrap>
              <Tag color="blue">{template.slug}</Tag>
              <Text type="secondary">
                <MailOutlined /> {t("emailManager.templateId")}: {template.id}
              </Text>
            </Space>
          </div>
        </div>

        {/* Template Details */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("emailManager.title")} span={2}>
            {template.title}
          </Descriptions.Item>

          <Descriptions.Item label={t("emailManager.subject")} span={2}>
            <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
              {template.subject}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label={t("emailManager.slug")} span={1}>
            <Tag color="purple">{template.slug}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("emailManager.templateId")} span={1}>
            {template.id}
          </Descriptions.Item>

          <Descriptions.Item label={t("common.createdAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDateTime(template.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")} span={1}>
            <Space>
              <ClockCircleOutlined />
              {formatDateTime(template.updated_at || template.created_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {/* Available Variables Section */}
        <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
          {t("emailManager.availableVariables")}
        </Divider>

        <Card
          size="small"
          style={{ backgroundColor: "#fafafa", marginBottom: "24px" }}
        >
          <div
            className="variables-list"
            style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
          >
            {getTemplateVariables(template.slug).map((variable, index) => (
              <div key={index} style={{ flex: "1 1 200px" }}>
                <Tag color="blue" style={{ marginBottom: "4px" }}>
                  <CodeOutlined /> {variable.name}
                </Tag>
                <div
                  style={{ fontSize: "12px", color: "#666", marginLeft: "4px" }}
                >
                  {variable.description}
                </div>
              </div>
            ))}
          </div>
          <Divider style={{ margin: "16px 0 8px" }} />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {t("emailManager.variablesHelp")}
          </Text>
        </Card>

        {/* Template Content Section */}
        <Divider orientation="left" style={{ margin: "16px 0" }}>
          {t("emailManager.templateContent")}
        </Divider>

        <div
          style={{
            backgroundColor: "#f5f5f5",
            padding: "20px",
            borderRadius: "6px",
            maxHeight: "500px",
            overflowY: "auto",
          }}
        >
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              backgroundColor: "#fff",
              padding: "15px",
              borderRadius: "4px",
              border: "1px solid #e8e8e8",
              fontFamily: "monospace",
              fontSize: "13px",
              lineHeight: "1.6",
            }}
          >
            {template.template_content}
          </pre>
        </div>

        {/* HTML Preview Section */}
        <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
          {t("emailManager.htmlPreview")}
        </Divider>

        <div
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              backgroundColor: "#f0f0f0",
              padding: "8px 16px",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Text strong>{t("emailManager.renderedPreview")}</Text>
          </div>
          <div
            style={{
              padding: "24px",
              backgroundColor: "#fff",
              maxHeight: "400px",
              overflowY: "auto",
            }}
            dangerouslySetInnerHTML={{
              __html:
                template.template_content ||
                `<p><em>${t("emailManager.noContent")}</em></p>`,
            }}
          />
        </div>

        {/* Timeline Section */}
        <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
          {t("emailManager.templateTimeline")}
        </Divider>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderRadius: "6px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <CalendarOutlined style={{ color: "#52c41a", fontSize: "16px" }} />
            <div>
              <Text strong>{t("common.createdAt")}</Text>
              <br />
              <Text type="secondary">
                {formatDateTime(template.created_at)}
              </Text>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <ClockCircleOutlined
              style={{ color: "#1890ff", fontSize: "16px" }}
            />
            <div>
              <Text strong>{t("common.lastUpdated")}</Text>
              <br />
              <Text type="secondary">
                {formatDateTime(template.updated_at || template.created_at)}
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EmailTemplateDetails;
